import type { Dictionary } from './nl.js'

/** Typed against the Dutch keys, so a missing translation is a build error. */
export const en: Dictionary = {
  app_name: 'Streepaf',
  home_title: 'Make a list. Share the link. Cross things off.',
  home_sub: 'No account. Together, live.',
  home_new: 'New list',
  home_new_busy: 'Working…',
  home_your_lists: 'Your lists',
  home_your_lists_hint: 'These live on this device only.',
  home_templates: 'Start from a template',
  home_how: 'How it works',
  home_step_1_title: 'Make',
  home_step_1_body: 'One tap and your list exists. No account, no email address.',
  home_step_2_title: 'Share',
  home_step_2_body: 'Send the link. Anyone who has it can join in.',
  home_step_3_title: 'Cross off',
  home_step_3_body: 'What one person ticks, everyone else sees straight away.',
  home_privacy_title: 'Privacy',
  home_privacy_body:
    'There are no accounts. Your list is reachable through a secret link, so only share it with people who should join. Lists that nobody touches for a year are deleted automatically.',
  home_devs: 'For developers and agents: API, OpenAPI and MCP',
  remove: 'Remove',
  remove_from_device: 'Remove from this list',
  role_edit: 'Can edit',
  role_view: 'Read only',
  unnamed: 'Untitled list',

  list_title_placeholder: 'Name your list',
  add_description: 'Add a description',
  description_placeholder: 'What is this list about?',
  add_item_placeholder: 'Add an item…',
  add_item: 'Add item',
  progress: '{checked} of {total} crossed off',
  progress_done: 'All crossed off',
  empty_title: 'Nothing to cross off yet',
  empty_body: 'Tip: paste a whole list of lines to add them all at once.',
  all_done_body: 'Done. Forgotten something? Add it below.',
  completed_group: 'Crossed off ({count})',
  completed_hidden: '{count} crossed off — show',
  section_add: 'Add a section',
  section_placeholder: 'Section name',

  item_edit: 'Edit',
  item_note: 'Note',
  item_note_placeholder: 'Note for this item',
  item_quantity: 'Quantity',
  item_quantity_placeholder: 'e.g. 500 g',
  item_claim: 'Who is bringing this?',
  item_claim_release: 'Never mind',
  item_delete: 'Delete',
  item_move_up: 'Move up',
  item_move_down: 'Move down',
  item_menu: 'More for this item',
  drag_handle: 'Drag to move',

  menu: 'Menu',
  menu_share: 'Share',
  menu_copy_text: 'Copy as text',
  menu_clear_completed: 'Clear crossed-off items',
  menu_uncheck_all: 'Uncheck everything',
  menu_sort_alpha: 'Sort A→Z',
  menu_duplicate: 'Duplicate',
  menu_copy_to_own: 'Copy into my own list',
  menu_export_md: 'Export as Markdown',
  menu_print: 'Print',
  menu_view: 'View',
  menu_hide_completed: 'Hide crossed off',
  menu_language: 'Language',
  menu_theme: 'Theme',
  menu_theme_auto: 'Automatic',
  menu_theme_light: 'Light',
  menu_theme_dark: 'Dark',
  menu_install: 'Install',
  menu_your_name: 'Your name',
  menu_info: 'About this list',
  menu_manage: 'Manage links',
  menu_delete_list: 'Delete list',
  menu_shortcuts: 'Keyboard shortcuts',

  share_title: 'Share',
  share_edit_tab: 'Edit together',
  share_view_tab: 'View only',
  share_edit_warning: 'Anyone with this link can edit the list.',
  share_view_warning: 'Anyone with this link can watch, but change nothing.',
  share_copy: 'Copy',
  share_copied: 'Copied',
  share_whatsapp: 'WhatsApp',
  share_more: 'Share…',
  share_qr: 'QR code',
  share_qr_hint: 'Scan to join in.',
  share_self_tip: 'Send the link to yourself too, so you cannot lose it.',

  manage_title: 'Manage links',
  manage_admin_warning:
    'This admin link is the only way to delete the list or replace a leaked link. Keep it private.',
  manage_rotate_edit: 'Issue a new edit link',
  manage_rotate_view: 'Issue a new view link',
  manage_rotate_warning: 'The old link stops working straight away.',
  manage_rotated: 'New link created.',

  info_created: 'Created on {date}',
  info_expires: 'Expires on {date} if nothing happens',
  info_items: '{count} items',

  view_only_banner: 'You are viewing this list. Ask whoever made it for the edit link to join in.',
  view_only_toast: 'This is a read-only link.',

  connection_live: 'Connected live',
  connection_saving: 'Saving…',
  connection_reconnecting: 'Reconnecting…',
  connection_offline_edit: 'No connection. Your changes are saved as soon as you are back online.',
  connection_offline_view: 'No connection. This is the last version we saw.',

  undo: 'Undo',
  toast_item_deleted: '‘{text}’ deleted',
  toast_items_added: '{count} items added',
  toast_completed_cleared: '{count} crossed-off items cleared',
  toast_unchecked_all: '{count} items unchecked',
  toast_copied: 'Link copied',
  toast_text_copied: 'List copied as text',
  toast_remote_cleared: 'Someone cleared {count} crossed-off items',
  toast_remote_changed: 'Someone else changed this list',
  toast_item_gone: 'Someone else deleted this item',
  toast_save_failed: 'Could not save: {reason}',
  toast_rate_limited: 'Slow down a moment, then try again.',
  toast_update_available: 'A new version is available',
  refresh: 'Refresh',

  confirm_delete_list_title: 'Delete this list?',
  confirm_delete_list_body: 'The list and all its items go for good. This cannot be undone.',
  confirm_delete_list_yes: 'Yes, delete it',
  confirm_clear_title: 'Clear {count} crossed-off items?',
  confirm_cancel: 'Cancel',

  name_prompt_title: 'What is your name?',
  name_prompt_body: 'So others know who brings what. Stored on this device only.',
  name_placeholder: 'Your name',
  save: 'Save',
  close: 'Close',
  back: 'Back',

  notfound_title: 'This list does not exist',
  notfound_body:
    'The link is wrong, or the list was deleted. Lists that nobody touches disappear on their own.',
  notfound_new: 'Make a new list',
  notfound_home: 'Go to the start page',
  error_title: 'Something went wrong',
  error_retry: 'Try again',
  load_failed: 'Cannot load the list',
  load_failed_body: 'Check your connection.',

  shortcuts_title: 'Keyboard shortcuts',
  shortcut_add: 'Jump to the add box',
  shortcut_toggle: 'Cross an item off',
  shortcut_edit: 'Edit an item',
  shortcut_move: 'Move an item',
  shortcut_delete: 'Delete an item',
  shortcut_help: 'This help',
}
